import { ApiResponseError, buildOrgUrl } from '@app/utils/api';

import {
  buildFileDownloadPath,
  buildFileMetadataPath,
  buildFileTextContentPath,
  loadFileArtifactPreview,
  parseFileTextSlice,
  parseUploadedFileRecord,
  type ExistingFilePreviewState,
  type LoadedFileArtifactPreview,
} from './chatArtifactUtils';

export interface FilePreviewTransport {
  basePath: string;
  orgId: string;
  fetchWithAuthRetry: (input: string, init?: RequestInit) => Promise<Response>;
}

export const parseFileResourceRef = (resourceRef?: string | null): string | null => {
  const trimmed = (resourceRef ?? '').trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.toLowerCase().startsWith('file:')) {
    const fileId = trimmed.slice('file:'.length).trim();
    return fileId || null;
  }
  return null;
};

export const downloadManagedFileBytes = async (
  transport: FilePreviewTransport,
  fileId: string,
  accept: string,
): Promise<Blob> => {
  const response = await transport.fetchWithAuthRetry(
    buildOrgUrl(transport.basePath, transport.orgId, buildFileDownloadPath(fileId)),
    {
      headers: { Accept: accept },
    },
  );
  if (!response.ok) {
    throw new ApiResponseError(response, `Failed to download file (status ${response.status}).`);
  }
  return response.blob();
};

export const loadManagedFilePreview = async (
  transport: FilePreviewTransport,
  fileId: string,
  existing?: ExistingFilePreviewState,
  full = false,
): Promise<LoadedFileArtifactPreview> =>
  loadFileArtifactPreview({
    fileId,
    full,
    existing,
    loadMetadata: async (candidateFileId) => {
      const response = await transport.fetchWithAuthRetry(
        buildOrgUrl(transport.basePath, transport.orgId, buildFileMetadataPath(candidateFileId)),
        {
          headers: { Accept: 'application/json' },
        },
      );
      if (!response.ok) {
        throw new ApiResponseError(response, `Failed to load file metadata (status ${response.status}).`);
      }
      return parseUploadedFileRecord(await response.json());
    },
    loadTextSlice: async (candidateFileId, startLine, maxLines) => {
      const response = await transport.fetchWithAuthRetry(
        buildOrgUrl(transport.basePath, transport.orgId, buildFileTextContentPath(candidateFileId, startLine, maxLines)),
        {
          headers: { Accept: 'application/json' },
        },
      );
      if (!response.ok) {
        throw new ApiResponseError(response, `Failed to load file (status ${response.status}).`);
      }
      return parseFileTextSlice(await response.json());
    },
    loadDownloadBlob: (candidateFileId, accept) => downloadManagedFileBytes(transport, candidateFileId, accept),
  });
